import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { getAllBrokerCarts, updateCartName } from 'api/operations/cartServiceV2';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { bpHistory } from 'global/bpHistory';
import ModalDelegate from 'store/general/modalDelegate';
import { getRootStoreInstance } from 'store/rootStore';
import { Cart } from 'types/cartServiceV2';

interface RenameProposalFields extends DefaultFormFields {
  cartName: FormControl<string>;
}

export class FavoriteProposalsPageStore {
  private _rootStore = getRootStoreInstance();
  private _isLoading = false;
  private _carts: Cart[] = [];
  private _currentCart: Pick<Cart, 'cartName' | 'id'> | null = null;

  public deleteModalDelegate = new ModalDelegate();
  public renameModalDelegate = new ModalDelegate();

  public renameForm: FormController<RenameProposalFields>;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get carts(): Cart[] {
    return this._carts;
  }

  public get currentCartName(): string {
    return this._currentCart?.cartName || '';
  }

  private _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private _setCurrentCart(cart: Pick<Cart, 'cartName' | 'id'> | null): void {
    this._currentCart = cart;
  }

  public openDeleteModal(cartName: string, cartId: string): void {
    this._setCurrentCart({ cartName, id: cartId });
    this.deleteModalDelegate.open();
  }

  public closeDeleteModal(): void {
    this._setCurrentCart(null);
    this.deleteModalDelegate.close();
  }

  public openRenameModal(cartName: string, cartId: string): void {
    this._setCurrentCart({ cartName, id: cartId });
    this.renameModalDelegate.open();
    this.renameForm.populateFields({ cartName });
  }

  public closeRenameModal(): void {
    this._setCurrentCart(null);
    this.renameModalDelegate.close();
    this.renameForm.resetForm();
  }

  // eslint-disable-next-line class-methods-use-this
  public redirectToBrowsing(cartId: string, groupId: string): void {
    bpHistory.push(`/browsing?groupId=${groupId}&cartId=${cartId}&type=template`);
  }

  public async getInitialData(): Promise<void> {
    this._setIsLoading(true);
    const carts = await getAllBrokerCarts();

    runInAction(() => {
      this._carts = carts;
      this._setIsLoading(false);
    });
  }

  public async renameProposal(): Promise<void> {
    if (!this._currentCart) {
      return;
    }

    await updateCartName(this._currentCart.id, this.renameForm.fields.cartName.value);

    runInAction(() => {
      this.closeRenameModal();
      void this.getInitialData();
    });
  }

  public async deleteProposal(): Promise<void> {
    if (!this._currentCart) {
      return;
    }

    this.deleteModalDelegate.setIsLoading(true);
    await this._rootStore.cartStoreV2.deleteCart(this._currentCart.id);

    runInAction(() => {
      this.closeDeleteModal();
      this.deleteModalDelegate.setIsLoading(false);
      void this.getInitialData();
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.renameForm = new FormController<RenameProposalFields>(
      { cartName: new FormControl('') },
      { onSubmit: this.renameProposal },
    );
  }
}

const FavoriteProposalsPageStoreContext = createContext(new FavoriteProposalsPageStore());

export const useFavoriteProposalsPageStore = (): FavoriteProposalsPageStore => {
  return useContext(FavoriteProposalsPageStoreContext);
};

export { FavoriteProposalsPageStoreContext };
